import React from 'react';
import styles from './DataTable.module.css';

/**
 * DataTable
 * Props:
 * - columns: [{ key, label, width?, accessor?, render? }]
 *   - width: number | string (px / %)
 *   - accessor(row): any          // read value (when not a direct prop)
 *   - render(value, row): node    // final cell render
 * - rows: any[]
 * - rowKey?: string | ((row, i) => string|number)
 * - sortBy?: string
 * - sortDir?: 'asc'|'desc'
 * - onSort?: (key: string) => void
 * - loading?: boolean
 * - error?: string
 * - actionCell?: (row) => React.ReactNode
 * - actionHeader?: string
 */
export default function DataTable({
    columns,
    rows,
    rowKey = 'id',
    sortBy,
    sortDir = 'asc',
    onSort,
    loading = false,
    error = '',
    actionCell,
    actionHeader = 'Actions',
}) {
    const safeRows = Array.isArray(rows) ? rows : [];

    const effectiveColumns = React.useMemo(() => {
        if (!actionCell) return columns;
        return [
            ...columns,
            {
                key: '__actions__',
                label: actionHeader,
                render: (_val, row) => actionCell(row),
                isAction: true,
            },
        ];
    }, [columns, actionCell, actionHeader]);

    const colgroupStyles = React.useMemo(() => {
        return effectiveColumns.map((col) => {
            if (col.isAction) return undefined;
            const w = col.width;
            return {
                width: typeof w === 'number' ? `${w}px` : String(w),
            };
        });
    }, [effectiveColumns]);

    const getRowKey = (row, i) =>
        typeof rowKey === 'function' ? rowKey(row, i) : (row?.[rowKey] ?? i);

    const getCellValue = (col, row) =>
        typeof col.accessor === 'function' ? col.accessor(row) : row?.[col.key];

    const tableLayout = actionCell ? 'auto' : 'fixed';

    return (
        <div className={styles.tableWrap}>
            <table className={styles.table} style={{ tableLayout }}>
                <colgroup>
                    {effectiveColumns.map((col, i) => (
                        <col key={col.key ?? i} style={colgroupStyles[i]} />
                    ))}
                </colgroup>

                <thead>
                    <tr>
                        {effectiveColumns.map((col) => {
                            const active = sortBy === col.key;

                            if (col.isAction) {
                                return (
                                    <th key={col.key} className={styles.actionsHeader}>
                                        {col.label}
                                    </th>
                                );
                            }

                            return (
                                <th
                                    key={col.key}
                                    scope="col"
                                    aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                >
                                    <button
                                        type="button"
                                        className={styles.thBtn}
                                        onClick={() => onSort && onSort(col.key)}
                                        aria-label={`Sort by ${col.label}`}
                                    >
                                        <span className={styles.thLabel}>{col.label}</span>
                                        <span
                                            className={`${styles.sort} ${active ? styles.active : ''} ${sortDir === 'asc' ? styles.asc : styles.desc}`}
                                        />
                                    </button>
                                </th>
                            );
                        })}
                    </tr>
                </thead>

                <tbody>
                    {loading && safeRows.length === 0 &&
                        Array.from({ length: 6 }).map((_, i) => (
                            <tr key={`sk-${i}`}>
                                <td className={styles.skeleton} colSpan={effectiveColumns.length} />
                            </tr>
                        ))
                    }

                    {!loading && !error && safeRows.length === 0 && (
                        <tr>
                            <td className={styles.empty} colSpan={effectiveColumns.length}>
                                No results
                            </td>
                        </tr>
                    )}

                    {safeRows.length > 0 &&
                        safeRows.map((row, rowIndex) => (
                            <tr key={getRowKey(row, rowIndex)}>
                                {effectiveColumns.map((col) => {
                                    const val = getCellValue(col, row);
                                    return (
                                        <td
                                            key={col.key}
                                            title={val == null ? '' : String(val)}
                                            className={col.isAction ? styles.actionsCell : undefined}
                                        >
                                            {col.render ? col.render(val, row) : val ?? '—'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    }

                    {error && (
                        <tr>
                            <td className={styles.error} colSpan={effectiveColumns.length}>
                                ⚠ {error}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
